import { plain } from '../../lib/labels';
import { Card, Descriptions, Empty, Space, Table, Tag, Typography } from 'antd';
import { ListenerName, UserName } from '../../lib/listenerName';
import { formatCallCharge, formatLedgerAmount, formatRupees, ledgerReasonLabel } from '../../lib/money';
import {
  disconnectReasonLabel,
  disconnectedByColor,
  disconnectedByLabel,
  formatDuration,
  formatWhen,
} from './callDisconnect';
import { CallRequestPanel } from './CallRequestPanel';
import { CallActivityTimeline } from '../logs/ActivityTimeline';
import { usePermissions } from '../../auth/store';
import {
  actorLabel,
  buildCallTimeline,
  connectionLabel,
  outcomeColor,
  outcomeLabel,
  outcomeReasonLabel,
  ringWasDelivered,
  terminalTrigger,
} from './callOutcome';

export type CallStatus =
  | 'initiated'
  | 'ringing'
  | 'connecting'
  | 'connected'
  | 'ended_normal'
  | 'ended_low_balance'
  | 'ended_dropped'
  | 'ended_flagged'
  | 'ended_rejected';

export const STATUS_COLOR: Record<CallStatus, string> = {
  initiated: 'default',
  ringing: 'processing',
  connecting: 'processing',
  connected: 'success',
  ended_normal: 'default',
  ended_low_balance: 'warning',
  ended_dropped: 'warning',
  ended_flagged: 'error',
  ended_rejected: 'default',
};

interface BillingTick {
  id: string;
  tickIndex: number;
  /** `partial` = a last minute billed pro-rata (call ended below the billing
   *  threshold). Absent on older API builds = a full minute. */
  kind?: 'full' | 'partial';
  /** Seconds a `partial` row covers. */
  seconds?: number | null;
  chargedPaise: string;
  chargedCoins?: number | null;
  /** Set when the minute's charge was given back (e.g. the call ended before it). */
  refundedAt?: string | null;
  occurredAt: string;
}

interface CallEvent {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  trigger: string | null;
  createdAt: string;
}

interface LedgerRow {
  id: string;
  accountId: string;
  /** Signed coin movement on coin-wallet rows; null on paise rows. */
  amountCoins?: number | null;
  amountPaise: string | null;
  direction: 'credit' | 'debit';
  reasonCode: string;
  idempotencyKey: string;
  createdAt: string;
}

/**
 * `GET /admin/calls/:id`. Everything past `id`/`status` is optional on purpose:
 * this is a claim about a server that may be older or newer than this build, and
 * about rows written years apart. The view must render whatever subset arrives.
 */
export interface CallDetail {
  id: string;
  status: CallStatus;
  mode?: 'audio' | 'video';
  userId?: string;
  listenerId?: string;
  /** displayName = the caller's own Display Name — absent on older APIs. */
  user?: { handle?: string | null; displayName?: string | null; avatarSeed?: string; phoneLast4?: string | null } | null;
  listener?: { handle?: string | null; displayName?: string | null; tier?: string | null } | null;
  ratePaisePerMin?: string | null;
  totalChargedCoins?: number | null;
  totalChargedPaise?: string | null;
  durationSeconds?: number | null;
  startedAt?: string | null;
  connectedAt?: string | null;
  endedAt?: string | null;
  /** Recorded at the terminal transition; null/absent on older calls. */
  disconnectedBy?: string | null;
  disconnectReason?: string | null;
  endTrigger?: string | null;
  /** When the listener accepted (null: never accepted). */
  acceptedAt?: string | null;
  /** What happened to the attempt, who did it, why (older calls: inferred, flagged). */
  outcome?: string | null;
  outcomeActor?: string | null;
  outcomeReason?: string | null;
  /** Raw terminal lifecycle trigger (API `outcomeTrigger`). Absent on an older
   *  response — the events below then supply it. */
  outcomeTrigger?: string | null;
  /** Did the listener's phone acknowledge the ring? (API `ringDelivered`.) */
  ringDelivered?: boolean;
  attributionInferred?: boolean;
  agoraChannel?: string | null;
  rtcProvider?: string | null;
  /** What the listener earned for this call (paise); absent on older API builds. */
  listenerSharePaise?: string | null;
  billedMinutes?: number | null;
  /** not_billable | verification | free_trial | billed | unbilled. */
  billingStatus?: string | null;
  /** The last call-setup stage the client completed — the step AFTER it is where setup failed. */
  failureStage?: string | null;
  failureReason?: string | null;
  /** Gifts sent during the call, when the API includes them. */
  giftCount?: number | null;
  giftTotalCoins?: number | null;
  giftTotalEarningPaise?: string | null;
  /** Retired Quick Connect call (old rows only). */
  isRandom?: boolean;
  /** Direct call with fallback: the request this ring belonged to and its number. */
  callRequestId?: string | null;
  attemptNo?: number | null;
  /**
   * The caller was an admin-flagged test account when the call STARTED:
   * 0 coins charged, ₹0 earned, no ledger rows, gifts off. Flipping the user
   * flag later never changes this.
   */
  isVerificationCall?: boolean;
  /** The ring went to a dummy (placeholder) listener: a timed attempt, never answered. */
  isDummyAttempt?: boolean;
  ratings?: Array<{ id: string; rating1To5: number; feedbackText: string | null; createdAt: string }>;
  billingTicks?: BillingTick[];
  events?: CallEvent[];
  ledger?: LedgerRow[];
  flags?: Array<{ id: string; severity: string; classifier: string; status: string; createdAt: string }>;
}

/**
 * Direct / fallback ring n / retired Quick Connect, prefixed "Admin verification · " on a
 * verification call and "Dummy attempt · " on a ring to a dummy listener.
 */
export function callTypeLabel(
  call: Pick<CallDetail, 'isRandom' | 'callRequestId' | 'attemptNo' | 'isVerificationCall' | 'isDummyAttempt'>,
): string {
  const base = call.isRandom
    ? 'Random (Quick Connect, retired)'
    : call.callRequestId
      ? call.attemptNo && call.attemptNo > 1
        ? `Direct — fallback ring ${call.attemptNo}`
        : 'Direct — picked buddy'
      : 'Direct';
  const withDummy = call.isDummyAttempt ? `Dummy attempt · ${base}` : base;
  return call.isVerificationCall ? `Admin verification · ${withDummy}` : withDummy;
}

/** What the money fields read on a verification call: there is no bill, ever. */
const VERIFICATION_ZERO = '0 (verification call — never billed)';

export function CallDetailView({ call }: { call: CallDetail }) {
  const { can } = usePermissions();
  const ticks = call.billingTicks ?? [];
  const events = call.events ?? [];
  const ledger = call.ledger ?? [];
  const flags = call.flags ?? [];
  const ratings = call.ratings ?? [];
  const ended = call.status.startsWith('ended_');

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Call ID" span={2}>
          <Typography.Text code copyable style={{ fontSize: 11 }}>
            {call.id}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="User">
          <UserName handle={call.user?.handle} displayName={call.user?.displayName} userId={call.userId} /> ••• {call.user?.phoneLast4 ?? '????'}
        </Descriptions.Item>
        <Descriptions.Item label="Listener">
          <ListenerName
            listener={{ displayName: call.listener?.displayName, handle: call.listener?.handle, id: call.listenerId }}
            listenerId={call.listenerId}
            secondary="inline"
          />
        </Descriptions.Item>
        <Descriptions.Item label="Mode">{call.mode ? <Tag>{plain(call.mode)}</Tag> : '—'}</Descriptions.Item>
        <Descriptions.Item label="Call type">
          {call.isVerificationCall || call.isDummyAttempt ? (
            <Space size={4} wrap>
              {call.isVerificationCall && <Tag color="purple">Admin Verification Call</Tag>}
              {call.isDummyAttempt && <Tag color="purple">Dummy listener attempt</Tag>}
              <span>{callTypeLabel(call)}</span>
            </Space>
          ) : (
            callTypeLabel(call)
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={STATUS_COLOR[call.status] ?? 'default'}>{plain(call.status)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Rate / min">
          {call.isVerificationCall ? VERIFICATION_ZERO : formatRupees(call.ratePaisePerMin)}
        </Descriptions.Item>
        <Descriptions.Item label="Total charged">
          {call.isVerificationCall ? VERIFICATION_ZERO : formatCallCharge(call)}
          {call.billingStatus && call.billingStatus !== 'billed' && !call.isVerificationCall ? (
            <Tag style={{ marginInlineStart: 6 }}>{plain(call.billingStatus)}</Tag>
          ) : null}
        </Descriptions.Item>
        {call.listenerSharePaise != null ? (
          <Descriptions.Item label="Listener earned">
            {call.isVerificationCall ? VERIFICATION_ZERO : formatRupees(call.listenerSharePaise)}
            {call.billedMinutes ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {' '}
                · {call.billedMinutes} billed min
              </Typography.Text>
            ) : null}
          </Descriptions.Item>
        ) : null}
        {call.giftCount ? (
          <Descriptions.Item label="Gifts in this call">
            {call.giftCount} · {call.giftTotalCoins ?? 0} coins
            {call.giftTotalEarningPaise != null ? ` · listener earned ${formatRupees(call.giftTotalEarningPaise)}` : ''}
          </Descriptions.Item>
        ) : null}
        {call.outcome !== undefined ? (
          <>
            <Descriptions.Item label="Connection">{connectionLabel(call.outcome, call.connectedAt)}</Descriptions.Item>
            <Descriptions.Item label="Outcome">
              <Tag color={outcomeColor(call.outcome)}>{outcomeLabel(call.outcome)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Action by">
              {actorLabel(call.outcomeActor)}
              {call.attributionInferred ? ' (from lifecycle)' : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Reached the phone">
              {(call.ringDelivered ?? ringWasDelivered(events))
                ? "Yes — the listener's phone acknowledged the ring"
                : 'Not evidenced — no acknowledgement from the device'}
            </Descriptions.Item>
            <Descriptions.Item label="Reason">
              {outcomeReasonLabel(
                call.outcomeReason,
                call.outcomeActor,
                call.connectedAt != null,
                // Prefer the API's field; fall back to the events this view
                // already loads, so older API responses still read correctly.
                call.outcomeTrigger ?? terminalTrigger(events),
                call.ringDelivered ?? ringWasDelivered(events),
              )}
            </Descriptions.Item>
          </>
        ) : null}
        <Descriptions.Item label="Start time">{formatWhen(call.startedAt)}</Descriptions.Item>
        {call.outcome !== undefined ? (
          <Descriptions.Item label="Accepted">
            {call.acceptedAt ? formatWhen(call.acceptedAt) : 'Not accepted'}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Connected">{formatWhen(call.connectedAt)}</Descriptions.Item>
        <Descriptions.Item label="End time">{formatWhen(call.endedAt)}</Descriptions.Item>
        <Descriptions.Item label="Duration">{formatDuration(call.durationSeconds)}</Descriptions.Item>
        <Descriptions.Item label="Call room (support reference)" span={2}>
          <code style={{ fontSize: 11 }}>{call.agoraChannel ?? '—'}</code>
          {call.rtcProvider ? <Tag style={{ marginInlineStart: 8 }}>{call.rtcProvider}</Tag> : null}
        </Descriptions.Item>
      </Descriptions>
      {call.callRequestId && <CallRequestPanel requestId={call.callRequestId} currentSessionId={call.id} />}

      <Card size="small" title="Disconnect">
        {ended ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Ended by">
              <Tag color={disconnectedByColor(call.disconnectedBy)}>
                {disconnectedByLabel(call.disconnectedBy)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Why it ended">
              {disconnectReasonLabel(
                call.disconnectReason,
                call.disconnectedBy,
                call.outcomeTrigger ?? call.endTrigger ?? terminalTrigger(events),
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Ended at">{formatWhen(call.endedAt)}</Descriptions.Item>
            {call.endTrigger ? (
              <Descriptions.Item label="What ended it">
                <code style={{ fontSize: 11 }}>{call.endTrigger}</code>
              </Descriptions.Item>
            ) : null}
            {call.failureStage || call.failureReason ? (
              <Descriptions.Item label="Setup stopped after">
                {call.failureStage ? <Tag color="orange">{plain(call.failureStage)}</Tag> : null}
                {call.failureReason ? <Typography.Text>{call.failureReason}</Typography.Text> : null}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">The call has not ended.</Typography.Text>
        )}
      </Card>

      <Card size="small" title={`Billing ticks (${ticks.length})`}>
        {ticks.length === 0 ? (
          <Empty description="No ticks." />
        ) : (
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={ticks}
            columns={[
              { title: 'Minute #', dataIndex: 'tickIndex', width: 80 },
              { title: 'When', dataIndex: 'occurredAt', render: (s: string) => formatWhen(s) },
              {
                title: 'Type',
                key: 'kind',
                render: (_: unknown, t: BillingTick) =>
                  t.kind === 'partial' ? (
                    <Tag color="blue">Partial{t.seconds != null ? ` · ${t.seconds} s` : ''}</Tag>
                  ) : t.refundedAt ? (
                    <Tag>Full · refunded</Tag>
                  ) : (
                    <Tag color="green">Full minute</Tag>
                  ),
                width: 140,
              },
              {
                title: 'Charged',
                key: 'charged',
                render: (_: unknown, t: BillingTick) => {
                  const amount = t.chargedCoins != null ? `${t.chargedCoins} coins` : formatRupees(t.chargedPaise);
                  return t.refundedAt ? <Typography.Text delete>{amount}</Typography.Text> : amount;
                },
                width: 110,
              },
            ]}
          />
        )}
      </Card>

      <Card size="small" title="Timeline">
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          dataSource={buildCallTimeline(call)}
          columns={[
            { title: 'When', dataIndex: 'at', width: 190, render: (s: string | null) => formatWhen(s) },
            { title: 'What happened', dataIndex: 'title' },
            { title: 'Done by', dataIndex: 'actor', width: 110, render: (v: string) => <Tag>{plain(v)}</Tag> },
          ]}
        />
      </Card>

      {can('logs.view') && (
        <Card size="small" title="Activity timeline (logs)">
          <CallActivityTimeline callId={call.id} />
        </Card>
      )}

      <Card size="small" title={`State events (${events.length})`}>
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={events}
          columns={[
            { title: 'When', dataIndex: 'createdAt', render: (s: string) => formatWhen(s) },
            { title: 'From', dataIndex: 'fromStatus', width: 140 },
            {
              title: 'To',
              dataIndex: 'toStatus',
              render: (v: string) => <Tag>{plain(v)}</Tag>,
              width: 140,
            },
            { title: 'What caused it', dataIndex: 'trigger' },
          ]}
        />
      </Card>

      <Card size="small" title={`Ledger entries (${ledger.length})`}>
        {ledger.length === 0 ? (
          <Empty description="No ledger entries (call never connected)." />
        ) : (
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={ledger}
            columns={[
              { title: 'When', dataIndex: 'createdAt', render: (s: string) => formatWhen(s) },
              {
                title: '',
                dataIndex: 'direction',
                render: (d: string) => <Tag color={d === 'credit' ? 'success' : 'error'}>{plain(d)}</Tag>,
                width: 80,
              },
              {
                title: 'Reason',
                dataIndex: 'reasonCode',
                render: (c: string) => <span title={c}>{ledgerReasonLabel(c)}</span>,
              },
              {
                title: 'Amount',
                key: 'amount',
                render: (_: unknown, row: LedgerRow) => formatLedgerAmount(row),
                width: 130,
              },
              {
                title: 'Request reference',
                dataIndex: 'idempotencyKey',
                render: (k: string) => <code style={{ fontSize: 11 }}>{k}</code>,
              },
            ]}
          />
        )}
      </Card>

      {flags.length > 0 ? (
        <Card size="small" title={`Moderation flags (${flags.length})`}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={flags}
            columns={[
              { title: 'When', dataIndex: 'createdAt', render: (s: string) => formatWhen(s) },
              {
                title: 'How serious',
                dataIndex: 'severity',
                render: (v: string) => (
                  <Tag color={v === 'critical' ? 'error' : v === 'high' ? 'warning' : 'default'}>{plain(v)}</Tag>
                ),
              },
              { title: 'Detected by', dataIndex: 'classifier' },
              { title: 'Status', dataIndex: 'status' },
            ]}
          />
        </Card>
      ) : null}

      {ratings.length > 0 ? (
        <Card size="small" title="Ratings">
          {ratings.map((r) => (
            <div key={r.id} style={{ marginBottom: 6 }}>
              <Tag color="gold">★ {r.rating1To5}</Tag>
              {r.feedbackText ? <span style={{ color: '#bbb' }}>{r.feedbackText}</span> : null}
            </div>
          ))}
        </Card>
      ) : null}
    </Space>
  );
}
